/**
 * CutPlan.jsx — Nirvikalp 4.0
 * Standalone Cut Plan module with multi-marker simulation engine.
 * Extracted from ProductionPlanning.js to keep file sizes manageable.
 *
 * Props: db, setDb, saveToDatabase, extractStylePOs, broadcastUpdate
 *
 * Process flow:
 *  Setup (style/PO/color) → Marker Plan → Roll Scan →
 *  Simulation (multi-marker) → Select best → Confirm & Save → Bundles + Lay Slip
 */

import React, { useState, useCallback, useMemo } from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────
const SIZES_ORDER = ['XXS','XS','S','M','L','XL','2XL','3XL','4XL','5XL','XXL'];
const API = 'https://nirvikalp.integrashion.com/api';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const parsePanels = (str = '') => {
  // Normalize separators: en/em-dash → hyphen, slash → hyphen, spaces around separators
  const s = str.replace(/[–—−‐‑]/g, '-').replace(/\s*\/\s*/g, '-').toUpperCase().trim();
  return (s.match(/([A-Z0-9]+)-(\d+)/g) || []).map(t => {
    const [sz, q] = t.split('-');
    return { size: sz, qty: parseInt(q) || 1 };
  });
};

const sortBySize = (arr, key = 'size') =>
  [...arr].sort((a, b) =>
    SIZES_ORDER.indexOf(a[key] ?? a) - SIZES_ORDER.indexOf(b[key] ?? b));

const fmtN = (n, d = 2) => (parseFloat(n) || 0).toFixed(d);
const fmtK = n => (parseInt(n) || 0).toLocaleString();

// ─── Roll field accessors ─────────────────────────────────────────────────────
// Fabric roll inventory may come from various sources with different column names.
// These helpers try all known variants so the cut plan works regardless of source.
// NOTE: In the standard import CSV, actual fabric length is in M_Qty (Balance_Qty is blank).
const getBalance = r => parseFloat(
  r.Balance_Qty   ?? r.balance_qty   ?? r.BalanceQty    ??
  r.M_Qty         ?? r.m_qty         ?? r.MQty          ??  // ← primary for CSV import
  r.FG_Cap        ?? r.fg_cap        ?? r.Gross_Qty     ?? r.gross_qty ??
  r.stock_qty     ?? r.Remaining     ?? r.remaining     ?? r.qty ?? r.Qty ?? 0);

const getRollId = r =>
  r.Roll_ID       ?? r['Roll_ID']    ??     // handles BOM-free
  r.roll_id       ?? r.RollID       ?? r.rollId ?? r.id ?? '—';

const getRollShade = r =>
  (r.Shade ?? r.shade ?? r.ShadeGroup ?? r.shade_group ?? r.Lot ?? r.lot ?? '').toString().trim();

const getRollWidth = r =>
  r.Width ?? r.width ?? r.FabricWidth ?? r.fabric_width ?? r.width_inch ?? 0;

const getRollStyle = r =>
  (r.Style_Name ?? r.style_name ?? r.Style ?? r.style ?? '').toString().trim();

const getRollOrder = r =>
  (r.Order_No ?? r.order_no ?? r.PO ?? r.po_no ?? r.poNo ?? '').toString().trim();

const getRollColor = r =>
  (r.Color ?? r.color ?? r.Colour ?? r.colour ?? '').toString().trim();

// Shrinkage accessors (stored as integers: 1 = 1%, 3 = 3%)
const getLengthShrink = r => parseFloat(r.Length_Shrinkage ?? r.length_shrinkage ?? r.LengthShrinkage ?? r.LShrink ?? 0) / 100;
const getWidthShrink  = r => parseFloat(r.Width_Shrinkage  ?? r.width_shrinkage  ?? r.WidthShrinkage  ?? r.WShrink  ?? 0) / 100;
const getPatternGroup = r => r.Pattern_Group ?? r.pattern_group ?? r.PatternGroup ?? r.CSV ?? r.csv ?? '';
const getCSVGroup     = r => r.CSV ?? r.csv ?? r.CSV_Group ?? r.csv_group ?? r.ShadeGroup ?? '';
const S = {
  wrap:    { fontFamily: 'sans-serif', background: '#f1f5f9', padding: '20px', minHeight: '100%' },
  card:    { background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '18px', marginBottom: '16px' },
  hdr:     { fontSize: '0.85rem', fontWeight: '800', color: '#1e3a8a', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  grid:    cols => ({ display: 'grid', gridTemplateColumns: cols, gap: '12px' }),
  lbl:     { fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' },
  inp:     { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.88rem', background: '#fff' },
  sel:     { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.88rem', background: '#fff' },
  btn:     (bg, fg = '#fff') => ({ padding: '9px 20px', background: bg, color: fg, border: 'none', borderRadius: '7px', fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem' }),
  badge:   (bg, fg) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '700', background: bg, color: fg }),
  TH:      { padding: '6px 10px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0', fontWeight: '700', fontSize: '0.72rem', color: '#475569', textAlign: 'left', whiteSpace: 'nowrap' },
  TD:      { padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '0.78rem', color: '#334155', verticalAlign: 'middle' },
  step:    active => ({ padding: '6px 16px', borderRadius: '20px', fontWeight: '700', fontSize: '0.78rem', cursor: 'pointer', background: active ? '#1e3a8a' : '#f1f5f9', color: active ? '#fff' : '#64748b', border: active ? 'none' : '1px solid #e2e8f0' }),
  pill:    (c) => ({ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', border: `1px solid ${c}44`, background: `${c}11`, color: c }),
};

const Fld = ({ label, children }) => (
  <div><div style={S.lbl}>{label}</div>{children}</div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SIMULATION ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * ratioScore — how well a marker's size ratios match the PO ratio.
 * Lower = better match.
 */
const ratioScore = (panels, required) => {
  const total = panels.reduce((s, p) => s + p.qty, 0);
  const reqTotal = Object.values(required).reduce((s, v) => s + v, 0);
  if (!total || !reqTotal) return Infinity;
  return panels.reduce((err, p) => {
    const markerRatio = p.qty / total;
    const reqRatio    = (required[p.size] || 0) / reqTotal;
    return err + Math.abs(markerRatio - reqRatio);
  }, 0);
};

// Width normaliser: values > 100 are almost certainly centimetres → convert to inches
const toInches = w => { const v = parseFloat(w || 0); return v > 100 ? +(v / 2.54).toFixed(1) : v; };

const filterRolls = (rollPool, marker, opts = {}) =>
  rollPool.filter(r => {
    // Length shrinkage: need more fabric per lay (pattern already compensates in dimensions)
    const lShrink = getLengthShrink(r);
    const effLen  = lShrink > 0 ? parseFloat(marker.length || 10) / (1 - lShrink) : parseFloat(marker.length || 10);
    if (getBalance(r) < effLen) return false;

    const shade = getRollShade(r);
    if (!opts.ignoreShade && marker.shade && shade && shade !== marker.shade) return false;

    // CSV group match
    const csvGrp = getCSVGroup(r);
    if (!opts.ignoreCSV && marker.csvType && csvGrp && csvGrp !== marker.csvType) return false;

    // Pattern group match — encodes width shrinkage compensation (e.g. PG3 = 3% width shrink pattern)
    const rollPG   = getPatternGroup(r);
    const markerPG = marker.patternGroup || marker.pattern || '';
    if (markerPG && rollPG && rollPG !== markerPG) return false;

    // Width in inches — 3% under-width tolerance (same as original behaviour),
    // upper cap at mw+2" to avoid excessive selvedge waste
    const mwIn = toInches(marker.width);
    const rwIn = toInches(getRollWidth(r));
    if (mwIn > 0 && rwIn > 0) {
      if (rwIn < mwIn * 0.97) return false; // more than 3% under-width
      if (rwIn > mwIn + 2)    return false; // more than 2" over-width
    }
    return true;
  });

/**
 * assignRolls — greedily assign rolls to meet totalPliesNeeded.
 * One roll per lay (standard), up to maxPlies per lay.
 * Returns { rolls, totalPlies, fabricUsed, endBit, lays }
 *
 * END BIT DEFINITION:
 *   Remaining fabric on a roll ONLY qualifies as an end bit when it is
 *   LESS THAN the marker lay length — i.e., it cannot yield even one more
 *   complete lay.  If remaining >= markerLen the roll goes back in the pool
 *   for the next lay; showing it as an "end bit" would be wrong.
 *
 *   Examples (markerLen = 12 m):
 *     roll 120 m → 10 lays, remainder 0  → end bit = 0 m   (no waste)
 *     roll 130 m → 10 lays, remainder 10 → end bit = 10 m  (< 12 ✓)
 *     roll 132 m → 11 lays, remainder 0  → end bit = 0 m   (no waste)
 *     roll  15 m →  1 lay,  remainder 3  → end bit = 3 m   (< 12 ✓)
 */
/**
 * assignRolls — Material Management algorithm ported to CutPlan.
 *
 * Exactly mirrors the MM Cut Optimiser:
 *   fullLays = floor(balance / layLen)        ← integer lays from this roll
 *   endBit   = balance − fullLays × layLen    ← always < layLen, never "continues"
 *
 * Rolls are sorted by the chosen strategy, then used sequentially:
 * Roll A supplies all its full lays, then Roll B, then C, etc.
 * This eliminates the "continues (8.12m left)" confusion — a roll with 8.12m
 * and layLen=7.5m gives exactly 1 lay + 0.62m end bit.
 *
 * Returns per-roll summary (not per-individual-lay) so the display mirrors MM.
 */
const assignRolls = (eligibleRolls, markerLen, maxPlies, totalPliesNeeded,
                     useEndBitRolls = false, rollStrategy = 'min_endbit') => {
  const EPS = 1e-6;

  // ── Per-roll analysis (identical to MM) ──────────────────────────────────
  const analyzed = eligibleRolls.map(r => {
    const rem      = (r.availableM !== undefined && r.availableM >= 0)
                     ? r.availableM : getBalance(r);
    // MM formula — no shrinkage adjustment on layLen
    const fullLays  = Math.floor((rem + EPS) / markerLen);
    const usedFab   = fullLays * markerLen;
    const endBit    = parseFloat((rem - usedFab).toFixed(6)); // always < markerLen
    const endBitPct = rem > 0 ? (endBit / rem * 100) : 0;
    return { ...r, rem, fullLays, usedFab, endBit, endBitPct };
  })
  .filter(r => r.fullLays > 0)   // must yield ≥ 1 complete lay
  .sort((a, b) => {
    if (rollStrategy === 'fewest_rolls')    return b.rem - a.rem;
    if (rollStrategy === 'min_endbit_pct') {
      const d = a.endBitPct - b.endBitPct;
      return Math.abs(d) > 0.01 ? d : b.rem - a.rem;
    }
    // min_endbit (default): smallest absolute end-bit first
    const d = a.endBit - b.endBit;
    return Math.abs(d) > EPS ? d : b.rem - a.rem;
  });

  // ── Greedy roll selection ─────────────────────────────────────────────────
  const rollResults = []; // per-roll summary, not per-lay
  let pliesAccum = 0;
  let layCounter = 1;

  for (const roll of analyzed) {
    if (pliesAccum >= totalPliesNeeded) break;

    // How many of this roll's full lays do we still need?
    const laysFromRoll  = Math.min(roll.fullLays, totalPliesNeeded - pliesAccum);
    const usedFromRoll  = laysFromRoll * markerLen;
    // End bit only lost when we exhaust the roll; partial use → remainder stays on shelf
    const wasteFromRoll = (laysFromRoll === roll.fullLays) ? roll.endBit : 0;
    const isPartial     = laysFromRoll < roll.fullLays;

    rollResults.push({
      rollId:       getRollId(roll),
      shade:        getRollShade(roll),
      width:        getRollWidth(roll),
      rack:         roll.Rack_No  || '—',
      cell:         roll.Cell_No  || '—',
      balance:      fmtN(roll.rem),          // available balance
      noLays:       fmtN(roll.rem / markerLen, 4),  // decimal (like MM "No Lays")
      fullLays:     roll.fullLays,           // integer full lays possible
      laysUsed:     laysFromRoll,            // actually used
      usedFromRoll: fmtN(usedFromRoll),
      endBit:       fmtN(roll.endBit),       // = balance − fullLays × layLen
      endBitPct:    fmtN(roll.endBitPct, 1),
      wasteFromRoll: fmtN(wasteFromRoll),    // 0 if partial use
      isPartial,
      layFrom:      layCounter,
      layTo:        layCounter + laysFromRoll - 1,
    });

    pliesAccum  += laysFromRoll;
    layCounter  += laysFromRoll;
  }

  const fabricUsed = pliesAccum * markerLen;
  // Total end-bit waste = sum of wasteFromRoll for fully-consumed rolls
  const endBitTot  = rollResults.reduce((s, r) => s + parseFloat(r.wasteFromRoll), 0);

  // ── End bit combined lay (optional) ──────────────────────────────────────
  let endBitLay = null;
  if (useEndBitRolls && endBitTot >= markerLen - EPS) {
    const ebRolls = analyzed.filter(r => r.endBit > EPS);
    endBitLay = {
      isEndBitLay: true,
      rollId:      'END BITS COMBINED',
      laysUsed:    1,
      usedFromRoll: fmtN(endBitTot),
      endBit:      '0.00',
      wasteFromRoll: '0.00',
      rolls: ebRolls.map(r => ({
        rollId: getRollId(r), endBit: fmtN(r.endBit), rem: fmtN(r.rem),
        rack: r.Rack_No, cell: r.Cell_No,
      })),
      note: `${fmtN(endBitTot)}m combined ≥ ${fmtN(markerLen)}m lay`,
    };
  }

  return { lays: rollResults, totalPlies: pliesAccum, fabricUsed, endBit: endBitTot, endBitLay };
};

/**
 * greedySimulate — core simulation for one strategy.
 * @param required    {size: qty}
 * @param markers     sorted by strategy (initial order hint)
 * @param rolls       full roll inventory (read-only)
 * @param maxPlies    max plies per lay
 * @param opts        {ignoreShade, ignoreCSV}
 * @param strategyId  'min_consumption' | 'max_efficiency' | 'best_ratio'
 *
 * STRATEGY SCORING (applied at every greedy step — this is what makes each option different):
 *   min_consumption → pick marker with LOWEST fabric consumption (m/pc) → saves fabric cost
 *   max_efficiency  → pick marker with HIGHEST cut-efficiency % → least end-bit waste
 *   best_ratio      → pick marker whose size ratio is CLOSEST to remaining PO ratio → fewest leftover sizes
 */
const greedySimulate = (required, markers, rolls, maxPlies, opts = {}, strategyId = 'max_efficiency') => {
  const useEndBitRolls = opts.useEndBitRolls || false;
  let remaining = { ...required };
  const usedRollM = {};
  const steps     = [];

  // Strategy-specific marker scorer applied at each greedy step
  const scoreMarker = (marker, panels) => {
    if (strategyId === 'min_consumption') {
      // Lower consumption = higher score → negate it
      return -(parseFloat(marker.consumption) || 99);
    }
    if (strategyId === 'max_efficiency') {
      // Higher efficiency % = higher score
      return parseFloat(marker.efficiency) || 0;
    }
    if (strategyId === 'best_ratio') {
      // Closest size ratio to remaining PO requirements = higher score → negate ratioScore
      return -ratioScore(panels, remaining);
    }
    // Default fallback
    return (parseFloat(marker.efficiency) || 0) / (parseFloat(marker.consumption) || 1);
  };

  const rollPool = () =>
    rolls.map(r => ({
      ...r,
      availableM: Math.max(0, getBalance(r) - (usedRollM[getRollId(r)] || 0)),
    }));

  let guard = 0;
  while (Object.values(remaining).some(q => q > 0) && guard++ < 30) {
    let picked = null;
    let pickScore = -Infinity;

    for (const marker of markers) {
      const panels = parsePanels(marker.cutPanels);
      if (!panels.length) continue;
      // Marker useful only if ALL its sizes still have remaining qty
      if (!panels.every(p => (remaining[p.size] || 0) > 0)) continue;

      // Max lays limited by remaining required per size
      const maxLaysBySizes = Math.min(...panels.map(p =>
        Math.floor((remaining[p.size] || 0) / p.qty)));
      if (maxLaysBySizes <= 0) continue;

      // MAX PLIES RULE: maxLaysBySizes is ALREADY the total plies needed
      // (= Math.floor(remaining[size] / panelQty) — gives plies directly).
      // Multiplying by maxPlies was a 38× overproduction bug.
      const eligible = filterRolls(rollPool(), marker, opts);
      const availFabric = eligible.reduce((s, r) => s + (r.availableM || 0), 0);
      const totalPliesCheck = maxLaysBySizes;
      const fabricNeeded = totalPliesCheck * parseFloat(marker.length || 10);
      const possiblePlies = Math.min(totalPliesCheck,
        Math.floor(availFabric / parseFloat(marker.length || 10)));
      if (possiblePlies <= 0) continue;

      const score = scoreMarker(marker, panels);
      if (score > pickScore) { pickScore = score; picked = { marker, panels, maxLaysBySizes, eligible, possiblePlies }; }
    }

    if (!picked) break;

    const { marker, panels, maxLaysBySizes: maxPly, eligible, possiblePlies: pp } = picked;
    const markerLen = parseFloat(marker.length || 10);
    // totalPliesNeeded = plies required to fulfill remaining PO qty, capped by fabric available
    // DO NOT multiply by maxPlies — maxLaysBySizes is already "total plies", not "total lays"
    const totalPliesNeeded = pp; // = Math.min(maxLaysBySizes, fabricAvailable/markerLen)

    const assignment = assignRolls(
      eligible, markerLen, maxPlies, totalPliesNeeded,
      useEndBitRolls,
      opts.rollStrategy || 'min_endbit'
    );
    if (assignment.totalPlies === 0) break; // no fabric left — stop

    // Pcs produced per size
    const pcsProduced = {};
    panels.forEach(p => { pcsProduced[p.size] = assignment.totalPlies * p.qty; });

    // Subtract from remaining
    Object.entries(pcsProduced).forEach(([sz, q]) => {
      remaining[sz] = Math.max(0, (remaining[sz] || 0) - q);
    });

    // Record fabric usage per roll
    assignment.lays.flatMap(l => l.rolls).forEach(r => {
      usedRollM[r.rollId] = (usedRollM[r.rollId] || 0) + parseFloat(r.usedM);
    });

    const totalPcsThisStep = Object.values(pcsProduced).reduce((s, q) => s + q, 0);
    steps.push({
      stepNo: steps.length + 1,
      markerNo: marker.markerNo,
      marker,
      markerLen,           // ← pass lay length so UI verify column can correctly determine end bits
      panels,
      lays: assignment.endBitLay ? [...assignment.lays, assignment.endBitLay] : assignment.lays,
      totalPlies: assignment.totalPlies,
      laysCount: assignment.lays.length,
      pcsProduced,
      totalPcsStep: totalPcsThisStep,
      fabricUsed: fmtN(assignment.fabricUsed),
      endBit: fmtN(assignment.endBit),
      consumption: totalPcsThisStep > 0 ? fmtN(assignment.fabricUsed / totalPcsThisStep) : '—',
      efficiency: marker.efficiency,
    });
  }

  const totalFabric = steps.reduce((s, st) => s + parseFloat(st.fabricUsed), 0);
  const totalEndBit = steps.reduce((s, st) => s + parseFloat(st.endBit), 0);
  const totalPcs    = steps.reduce((s, st) => s + st.totalPcsStep, 0);
  const totalPcsRequired = Object.values(required).reduce((s, v) => s + v, 0);
  const pcsComplete = steps.reduce((acc, st) => {
    Object.entries(st.pcsProduced).forEach(([sz, q]) => { acc[sz] = (acc[sz] || 0) + q; });
    return acc;
  }, {});

  return {
    steps, remaining,
    totalFabric: fmtN(totalFabric),
    totalEndBit: fmtN(totalEndBit),
    totalPcs, totalPcsRequired,
    overallConsumption:  totalPcs > 0 ? fmtN(totalFabric / totalPcs) : '—',
    netConsumption:      totalPcs > 0 ? fmtN((totalFabric - totalEndBit) / totalPcs) : '—',
    endBitPct:           totalFabric > 0 ? fmtN(totalEndBit / totalFabric * 100, 1) : '0',
    isComplete:          Object.values(remaining).every(q => q <= 0),
    pcsComplete,
    markersUsed:         [...new Set(steps.map(s => s.markerNo))],
    utilization:         totalFabric > 0 ? fmtN((totalFabric - totalEndBit) / totalFabric * 100, 1) : '0',
  };
};

/**
 * runSimulation — generates top-3 scenarios using different strategies.
 */
const runSimulation = (required, markers, rolls, maxPlies, opts = {}) => {
  const strategies = [
    {
      id: 'min_consumption',
      label: '🎯 Min Consumption',
      desc: 'Lowest m/pc — minimizes fabric cost',
      sort: (a, b) => parseFloat(a.consumption) - parseFloat(b.consumption),
    },
    {
      id: 'max_efficiency',
      label: '⚡ Max Efficiency',
      desc: 'Highest % marker efficiency — minimizes end-bits',
      sort: (a, b) => parseFloat(b.efficiency) - parseFloat(a.efficiency),
    },
    {
      id: 'best_ratio',
      label: '📐 Best Size Ratio',
      desc: 'Marker ratio closest to PO ratio — fewest leftover sizes',
      sort: (a, b) => ratioScore(parsePanels(a.cutPanels), required) -
                      ratioScore(parsePanels(b.cutPanels), required),
    },
  ];

  return strategies.map(st => ({
    ...st,
    result: greedySimulate(required, [...markers].sort(st.sort), rolls, maxPlies, opts, st.id),
  })).sort((a, b) =>
    parseFloat(a.result.overallConsumption) - parseFloat(b.result.overallConsumption)
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function CutPlan({ db, setDb, saveToDatabase, extractStylePOs, broadcastUpdate }) {
  // ── Step navigation ─────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ── Step 1: Setup ────────────────────────────────────────────────────────────
  const [setup, setSetup] = useState({ brand:'', season:'', style:'', po:'', color:'' });
  const [sizeBreakdown, setSizeBreakdown] = useState([]); // [{size, qty, fromPO}]
  const [alreadyCut,  setAlreadyCut]  = useState({});    // {size: qty already cut}
  const [poAuthorized,setPoAuthorized]= useState({});    // {size: PO qty}

  // ── Step 2: Markers ──────────────────────────────────────────────────────────
  const [markers, setMarkers] = useState([]);
  const [markerDraft, setMarkerDraft] = useState({
    markerNo:'', patternCode:'', patternGroup:'', width:'', length:'', way:'1-Way',
    csvType:'', cutPanels:'', markerYield:'', efficiency:'', consumption:'', wastage:'0', shrinkageL:'0', shrinkageW:'0',
  });

  // ── Step 3: Rolls ────────────────────────────────────────────────────────────
  const [eligibleRolls, setEligibleRolls] = useState([]);
  const [opts, setOpts] = useState({ ignoreShade:false, ignoreCSV:false, maxPlies:38, pliesDev:5 });

  // ── Step 4: Simulation ───────────────────────────────────────────────────────
  const [scenarios, setScenarios] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);

  // ── Step 5: Saved plans ──────────────────────────────────────────────────────
  const [savedPlans, setSavedPlans] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [layStatus, setLayStatus] = useState({});

  // ── Alert ────────────────────────────────────────────────────────────────────
  const [alert, setAlert] = useState('');

  // ─── Derived data ────────────────────────────────────────────────────────────
  const inventory    = useMemo(() => db?.materials || [], [db]);
  const allStyles    = useMemo(() => db?.mgmtStyles || [], [db]);
  const brands       = useMemo(() => db?.brands || [], [db]);
  const seasons      = useMemo(() => db?.seasons || [], [db]);
  const stylesForBrand = useMemo(() =>
    setup.brand ? allStyles.filter(s => s.brand === setup.brand) : allStyles, [allStyles, setup.brand]);
  const colorOptions = useMemo(() => {
    if (!setup.style) return [];
    const fromPOTable = (db?.poSizeTable || [])
      .filter(r => r.style_ref === setup.style && (!setup.po || r.po_no === setup.po))
      .map(r => r.color).filter(Boolean);
    if (fromPOTable.length) return [...new Set(fromPOTable)];
    const styleObj = allStyles.find(s => s.styleRef === setup.style);
    const fromPOs  = extractStylePOs ? extractStylePOs(styleObj || {}).map(p => p.col).filter(Boolean) : [];
    return [...new Set([...fromPOs, ...inventory.filter(r => getRollStyle(r) === setup.style).map(r => getRollColor(r)).filter(Boolean)])];
  }, [setup.style, setup.po, db, allStyles, inventory, extractStylePOs]);

  const poOptions = useMemo(() => {
    if (!setup.style) return [];
    const fromTable = [...new Set((db?.poSizeTable || []).filter(r => r.style_ref === setup.style).map(r => r.po_no).filter(Boolean))];
    if (fromTable.length) return fromTable;
    const styleObj = allStyles.find(s => s.styleRef === setup.style);
    return extractStylePOs ? extractStylePOs(styleObj || {}).map(p => ({ num: p.num, qty: p.qty, label: `${p.num}${p.col?' · '+p.col:''} (${fmtK(p.qty)} pcs)` })) : [];
  }, [setup.style, db, allStyles, extractStylePOs]);

  // ─── Load size breakdown from PO data ────────────────────────────────────────
  const loadSizeBreakdown = useCallback((style, po, color) => {
    // Source 1: poSizeTable
    const rows = (db?.poSizeTable || []).filter(r =>
      r.style_ref === style &&
      (!po    || r.po_no === po) &&
      (!color || (r.color||'').toLowerCase() === (color||'').toLowerCase())
    );
    if (rows.length > 0) {
      const map = {};
      rows.forEach(r => { if (r.size) map[r.size] = (map[r.size]||0) + (parseInt(r.qty)||0); });
      const bd = sortBySize(Object.entries(map).map(([size,qty]) => ({ size, qty, fromPO: true })));
      if (bd.length) { setSizeBreakdown(bd); return bd; }
    }
    // Source 2: style registry
    const styleObj = allStyles.find(s => s.styleRef === style);
    if (styleObj && extractStylePOs) {
      const pos  = extractStylePOs(styleObj);
      const tPO  = pos.find(p => p.num === po) || pos[0];
      const sb   = tPO?.sizeBreakdown;
      if (Array.isArray(sb) && sb.length) {
        const bd = sortBySize(sb.filter(s => parseInt(s.qty||s.Qty)>0).map(s => ({ size: s.size||s.Size, qty: parseInt(s.qty||s.Qty), fromPO: true })));
        if (bd.length) { setSizeBreakdown(bd); return bd; }
      }
    }
    setSizeBreakdown([]);
    setAlert('⚠ No size breakdown found. Upload PO size data in Merchandising first.');
    return [];
  }, [db, allStyles, extractStylePOs]);

  // ─── Load already-cut quantities from saved plans ─────────────────────────────
  const loadAlreadyCut = useCallback((style, po, color) => {
    const ac = {};
    const auth = {};
    (db?.cutPlans || db?.stylePlans || [])
      .filter(p => p.type === 'CUT_PLAN' && p.style === style && p.po === po && p.color === color)
      .forEach(plan => {
        Object.entries(plan.pcsComplete || plan.totalPieces || {}).forEach(([sz, q]) => {
          ac[sz] = (ac[sz]||0) + (parseInt(q)||0);
        });
      });
    (db?.poSizeTable || []).filter(r =>
      r.style_ref === style && (!po || r.po_no === po) && (!color || (r.color||'').toLowerCase() === (color||'').toLowerCase())
    ).forEach(r => { if (r.size) auth[r.size] = (auth[r.size]||0) + (parseInt(r.qty)||0); });
    setAlreadyCut(ac);
    setPoAuthorized(auth);
    return { ac, auth };
  }, [db]);

  // ─── Scan eligible rolls ─────────────────────────────────────────────────────
  const scanRolls = useCallback(() => {
    if (!setup.style) { setAlert('⚠ Select a style first.'); return; }

    const allRolls = inventory; // = db.materials
    const sampleRoll = allRolls[0];
    const sampleFields = sampleRoll ? Object.keys(sampleRoll).join(', ') : 'none';

    const rolls = allRolls.filter(r => {
      if (getBalance(r) <= 0) return false;
      if (setup.style && getRollStyle(r) !== setup.style) return false;
      if (setup.color && getRollColor(r) && getRollColor(r).toLowerCase() !== setup.color.toLowerCase()) return false;
      if (setup.po && getRollOrder(r) && getRollOrder(r) !== setup.po) return false;
      return true;
    });

    if (allRolls.length === 0) {
      setAlert(`❌ No fabric inventory found in database. Go to Material Management and add fabric rolls with style "${setup.style}".`);
      return;
    }

    if (rolls.length === 0) {
      // Diagnose: how many have balance > 0?
      const withBalance = allRolls.filter(r => getBalance(r) > 0);
      const forStyle    = allRolls.filter(r => getRollStyle(r) === setup.style);
      let msg = `❌ No matching rolls found for Style="${setup.style}", Color="${setup.color}".`;
      if (withBalance.length === 0) {
        msg += ` All ${allRolls.length} rolls have 0 balance. Available data fields: [${sampleFields}]. `;
        msg += `Balance field searched: Balance_Qty, FG_Cap, Gross_Qty, stock_qty, Remaining.`;
      } else if (forStyle.length === 0) {
        msg += ` ${withBalance.length} rolls have fabric but none linked to style "${setup.style}". Check Style_Name field.`;
      } else {
        msg += ` ${forStyle.length} rolls for this style but wrong color/PO filter.`;
      }
      setAlert(msg);
      return;
    }

    setEligibleRolls(rolls);
    const total = rolls.reduce((s, r) => s + getBalance(r), 0);
    const widths = [...new Set(rolls.map(r => toInches(getRollWidth(r))).filter(Boolean))];
    const shades = [...new Set(rolls.map(r => getRollShade(r)).filter(Boolean))];
    const markerWidths = markers.map(m => toInches(m.width)).filter(Boolean);
    const widthWarning = markerWidths.length > 0 && widths.length > 0
      ? markerWidths.some(mw => widths.every(rw => rw < mw || rw > mw + 2))
        ? ` ⚠ Width mismatch: markers need ${[...new Set(markerWidths)].join('/')}in, rolls are ${widths.join('/')}in — update marker width.`
        : ''
      : '';
    setAlert(`✓ ${rolls.length} rolls · ${fmtN(total)}m total · Roll widths: ${widths.join(', ')}in · Shades: ${shades.join(', ') || 'none'}${widthWarning}`);
  }, [inventory, setup]);

  // ─── Run simulation ──────────────────────────────────────────────────────────
  const runSim = useCallback(() => {
    if (!markers.length)     { setAlert('⚠ Add at least one marker.'); return; }
    if (!eligibleRolls.length){ setAlert('⚠ Scan rolls first.'); return; }

    // Build required from PO balance
    const required = {};
    const { ac, auth } = loadAlreadyCut(setup.style, setup.po, setup.color);

    if (Object.keys(auth).length > 0) {
      Object.entries(auth).forEach(([sz, authQty]) => {
        const balance = authQty - (ac[sz]||0);
        if (balance > 0) required[sz] = balance;
      });
    } else if (sizeBreakdown.length > 0) {
      sizeBreakdown.forEach(s => { if (parseInt(s.qty)>0) required[s.size] = parseInt(s.qty); });
    } else {
      setAlert('⚠ No PO or size data. Load from PO first.'); return;
    }

    if (!Object.keys(required).length) {
      setAlert('✅ All PO quantities already cut. No balance remaining.'); return;
    }

    const maxPlies  = parseInt(opts.maxPlies) || 38;
    const simResult = runSimulation(required, markers, eligibleRolls, maxPlies, {
      ignoreShade: opts.ignoreShade, ignoreCSV: opts.ignoreCSV,
    });

    setScenarios(simResult);
    setSelectedScenario(simResult[0]);
    setStep(4);

    const best = simResult[0].result;
    const shortSizes = Object.entries(best.remaining || {}).filter(([,q])=>q>0);
    const totalFabric = eligibleRolls.reduce((s,r) => s + getBalance(r), 0);
    const markerWidths = [...new Set(markers.map(m => parseFloat(m.width||0)).filter(Boolean))];
    const rollWidths   = [...new Set(eligibleRolls.map(r => parseFloat(getRollWidth(r)||0)).filter(Boolean))];
    const widthMismatch = markerWidths.length > 0 && rollWidths.length > 0 &&
      markerWidths.every(mw => rollWidths.every(rw => rw < mw || rw > mw + 2));

    const msg = [
      `✓ ${simResult.length} strategies. Best: ${simResult[0].label} — ${best.overallConsumption} m/pc.`,
      best.isComplete
        ? '✅ Full PO can be cut.'
        : `⚠ SHORT: ${shortSizes.map(([s,q])=>`${s}:${q}pcs`).join(', ')} cannot be fulfilled.`,
      widthMismatch ? `❌ Width mismatch — markers need ${markerWidths.join('/')}in but rolls are ${rollWidths.join('/')}in.` : '',
      !best.isComplete && totalFabric < 100 ? '❌ Total roll fabric very low — check Balance_Qty/FG_Cap field in inventory.' : '',
      !best.isComplete && best.markersUsed?.length === 0 ? '❌ No markers matched eligible rolls — check shade, width, and marker length.' : '',
    ].filter(Boolean).join(' · ');
    setAlert(msg);
  }, [markers, eligibleRolls, setup, sizeBreakdown, opts, loadAlreadyCut]);

  // ─── Confirm plan + save ─────────────────────────────────────────────────────
  const confirmPlan = useCallback(async (scenario) => {
    const r = scenario.result;
    if (!r.isComplete) {
      const shortLines = Object.entries(r.remaining).filter(([,q])=>q>0)
        .map(([sz,q])=>`  • ${sz}: ${q} pcs still needed`).join('\n');
      if (!window.confirm(`⚠ SHORT PLAN\n${shortLines}\n\nFabric shortfall. Proceed with partial plan?`)) return;
    }

    const plan = {
      type: 'CUT_PLAN', style: setup.style, po: setup.po, color: setup.color,
      name: `CutPlan-${setup.style}-${setup.po}-${setup.color}`,
      strategy: scenario.label, steps: r.steps,
      totalFabric: r.totalFabric, totalEndBit: r.totalEndBit,
      totalPcs: r.totalPcs, overallConsumption: r.overallConsumption,
      netConsumption: r.netConsumption, utilization: r.utilization,
      pcsComplete: r.pcsComplete, remaining: r.remaining,
      isComplete: r.isComplete, status: r.isComplete ? 'READY' : 'SHORT',
      sizeBreakdown, markersUsed: r.markersUsed,
      generatedAt: new Date().toLocaleString(),
      laysCompleted: 0,
    };

    setStep(5);
    setAlert('Saving to database…');

    try {
      const saved = await saveToDatabase('common', plan);
      const id = saved?.id || `local_${Date.now()}`;
      const full = { ...plan, id };
      setSavedPlans(prev => [...prev.filter(p => p.name !== plan.name), full]);
      setDb(prev => {
        // Issue rolls used in this plan — updates Material Management dashboard immediately
        const updatedMaterials = (prev.materials || []).map(roll => {
          let usedQty = 0;
          (plan.steps || []).forEach(st => {
            (st.lays || []).forEach(lr => {
              // st.lays is now per-roll (MM style): check rollId match
              if (lr.rollId === getRollId(roll) || lr.rollId === roll.Roll_ID) {
                usedQty += parseFloat(lr.usedFromRoll || 0);
              }
            });
          });
          if (usedQty <= 0) return roll;
          const curIssued  = parseFloat(roll.Issue_Qty || 0);
          const newIssued  = parseFloat((curIssued + usedQty).toFixed(4));
          const received   = parseFloat(roll.Received_Qty || roll.M_Qty || 0);
          const newBalance = Math.max(0, received - newIssued);
          const updated = { ...roll, Issue_Qty: newIssued.toString(),
            Balance_Qty: newBalance.toFixed(4), lock_status: 'issued',
            locked_plan_ref: full.id || full.name };
          if (saveToDatabase) saveToDatabase('material', updated).catch(e =>
            console.warn('[CutPlan] roll update failed:', e.message));
          return updated;
        });
        return {
          ...prev,
          materials:  updatedMaterials,
          cutPlans:   [...(prev.cutPlans||[]).filter(p=>p.name!==plan.name), full],
          stylePlans: [...(prev.stylePlans||[]).filter(p=>p.name!==plan.name), full],
        };
      });
      broadcastUpdate && broadcastUpdate('CUTPLAN_SAVED', full);
      setAlert(`✓ Plan saved (ID: ${id}). Rolls marked as issued. ${r.isComplete?'':'⚠ SHORT — '} Markers: ${r.markersUsed.join(', ')}`);
    } catch(e) {
      setAlert(`Plan ready but not saved to DB: ${e.message}`);
    }
  }, [setup, sizeBreakdown, setDb, saveToDatabase, broadcastUpdate]);

  // ─── Marker CSV import ───────────────────────────────────────────────────────
  const importMarkersCSV = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result.replace(/^\uFEFF/,'');
      const rows = text.split(/\r?\n/).map(r=>r.split(','));
      const hdr  = rows[0].map(h=>h.trim().replace(/"/g,''));
      const data = rows.slice(1).filter(r=>r.length>2&&r[0].trim()).map(cols=>{
        const obj={}; hdr.forEach((h,i)=>{ if(cols[i]!==undefined) obj[h]=cols[i].trim().replace(/"/g,''); });
        return {
          markerNo: obj.MarkerNo||obj.Marker_No||'', patternCode: obj.PatternCode||obj.Pattern_Code||'', patternGroup: obj.PatternGroup||obj.Pattern_Group||obj.patternGroup||'',
          width: obj.Width||'', length: obj.Length||'', way: obj.Way||'1-Way',
          csvType: obj.CSV_Type||obj.CSVType||'', cutPanels: obj.CutPanels||obj.Cut_Panels||'',
          markerYield: obj.MarkerYield||obj.Marker_Yield||'', efficiency: obj.Efficiency||'',
          consumption: obj.Consumption||'', wastage: obj.Wastage||'0',
          shrinkageL: obj.ShrinkageL||obj.Shrinkage_L||'0', shrinkageW: obj.ShrinkageW||'0',
        };
      }).filter(m=>m.markerNo&&m.cutPanels);
      setMarkers(prev => {
        const map = {...Object.fromEntries(prev.map(m=>[m.markerNo,m]))};
        data.forEach(m=>{ map[m.markerNo]=m; });
        return Object.values(map);
      });
      setAlert(`✓ ${data.length} markers imported. Check cut panels format (e.g. S-1 XL-2 XXL-1).`);
    };
    reader.readAsText(file); e.target.value='';
  };

  // ─── Print report ────────────────────────────────────────────────────────────
  const printPlan = (plan) => {
    const w = window.open('','_blank','width=900,height=700');
    const html = `<!DOCTYPE html><html><head><title>Cut Plan Report</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:11px;margin:24px;color:#111}
      h1{font-size:15px;margin-bottom:4px}h2{font-size:12px;margin:14px 0 6px;border-bottom:1px solid #ccc;padding-bottom:3px}
      table{width:100%;border-collapse:collapse;margin-bottom:14px}
      th{background:#f0f0f0;padding:5px 8px;border:1px solid #ccc;font-size:10px;text-align:left}
      td{padding:5px 8px;border:1px solid #ddd;font-size:10px}
      .grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:4px 20px;font-size:11px;margin-bottom:12px}
      .badge{display:inline-block;padding:1px 6px;border-radius:10px;font-size:9px;font-weight:bold;
             background:${plan.isComplete?'#dcfce7':'#fee2e2'};color:${plan.isComplete?'#166534':'#991b1b'}}
      @media print{.no-print{display:none}button{display:none}}
    </style></head><body>
    <div style="display:flex;justify-content:space-between">
      <div><h1>✂ CUT PLAN REPORT <span class="badge">${plan.status}</span></h1>
      <div style="font-size:10px;color:#64748b">Generated: ${plan.generatedAt} · Strategy: ${plan.strategy}</div></div>
      <button class="no-print" onclick="window.print()" style="padding:6px 14px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer">🖨 Print</button>
    </div>
    <div class="grid" style="margin-top:10px">
      <span>Style:</span><strong colspan="2">${plan.style}</strong>
      <span>PO:</span><strong>${plan.po}</strong>
      <span>Color:</span><strong>${plan.color}</strong>
      <span>Total Pcs:</span><strong>${fmtK(plan.totalPcs)}</strong>
      <span>Overall Consumption:</span><strong>${plan.overallConsumption} m/pc</strong>
      <span>Net Consumption:</span><strong>${plan.netConsumption} m/pc</strong>
      <span>Total Fabric:</span><strong>${plan.totalFabric} m</strong>
      <span>End Bits:</span><strong>${plan.totalEndBit} m</strong>
      <span>Utilization:</span><strong>${plan.utilization}%</strong>
    </div>
    <h2>SIZE BREAKDOWN</h2>
    <table><tr>${Object.keys(plan.pcsComplete||{}).map(s=>`<th>${s}</th>`).join('')}<th>TOTAL</th></tr>
    <tr>${Object.values(plan.pcsComplete||{}).map(q=>`<td><b>${fmtK(q)}</b></td>`).join('')}<td><b>${fmtK(plan.totalPcs)}</b></td></tr></table>
    <h2>MARKER STEPS</h2>
    <table><tr><th>#</th><th>Marker</th><th>Lays</th><th>Plies</th><th>Pcs</th><th>Fabric</th><th>End-Bit</th><th>m/pc</th><th>Eff%</th></tr>
    ${(plan.steps||[]).map(st=>`<tr>
      <td>${st.stepNo}</td><td><b>${st.markerNo}</b><br><small>${Object.entries(st.pcsProduced||{}).map(([s,q])=>`${s}:${q}`).join(' ')}</small></td>
      <td>${st.laysCount}</td><td>${st.totalPlies}</td><td><b>${fmtK(st.totalPcsStep)}</b></td>
      <td>${st.fabricUsed}m</td><td style="color:red">${st.endBit}m</td>
      <td>${st.consumption}</td><td>${st.efficiency}%</td>
    </tr>`).join('')}</table>
    <h2>LAY + ROLL DETAILS</h2>
    <table><tr><th>Step</th><th>Lay#</th><th>Roll ID</th><th>Shade</th><th>Width</th><th>Rack/Cell</th><th>Balance</th><th>Plies</th><th>Used</th><th>End-Bit</th><th>Done ☐</th></tr>
    ${(plan.steps||[]).flatMap(st=>st.lays.flatMap(lay=>lay.rolls.map(r=>`<tr>
      <td>${st.markerNo}</td><td>Lay#${lay.layNo}</td><td><b>${r.rollId}</b></td>
      <td>${r.shade||'—'}</td><td>${r.width||'—'}"</td><td>${r.rack||'—'}/${r.cell||'—'}</td>
      <td>${r.totalLen}m</td><td><b>${r.plies}</b></td><td>${r.usedM}m</td>
      <td style="color:red;font-weight:bold">${r.endBitM}m</td><td>☐</td>
    </tr>`))).join('')}</table>
    <p style="font-size:9px;color:#888;margin-top:20px">Nirvikalp 4.0 · ${plan.generatedAt}</p>
    </body></html>`;
    w.document.write(html); w.document.close();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.wrap}>
      {/* ── Header ── */}
      <div style={{ background:'linear-gradient(135deg,#0f172a,#1e3a8a)', borderRadius:'10px', padding:'16px 22px', color:'#fff', marginBottom:'16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <div style={{ fontSize:'1.15rem', fontWeight:'900', letterSpacing:'1px' }}>✂ AUTOMATED CUT PLAN</div>
          <div style={{ fontSize:'0.78rem', color:'#93c5fd', marginTop:'3px' }}>Multi-marker simulation · PO/Color/Size validated · End-bit minimization · Bundle & Lay slip</div>
        </div>
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
          {['SETUP','MARKERS','ROLLS','SIMULATE','PLAN'].map((lbl,i) => (
            <button key={lbl} onClick={()=>setStep(i+1)} style={S.step(step===i+1)}>
              {step>i+1?'✓ ':''}{lbl}
            </button>
          ))}
        </div>
      </div>

      {/* ── Alert ── */}
      {alert && (
        <div style={{ padding:'10px 16px', background: alert.startsWith('⚠')||alert.startsWith('❌')?'#fef2f2':'#f0fdf4',
                      border:`1px solid ${alert.startsWith('⚠')||alert.startsWith('❌')?'#fca5a5':'#86efac'}`,
                      borderRadius:'8px', marginBottom:'12px', fontSize:'0.82rem',
                      color: alert.startsWith('⚠')||alert.startsWith('❌')?'#991b1b':'#166534',
                      display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <span>{alert}</span>
          <button onClick={()=>setAlert('')} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:'1rem', lineHeight:1, marginLeft:'8px' }}>✕</button>
        </div>
      )}

      {/* ── Saved Plans toggle ── */}
      <div style={{ display:'flex', gap:'10px', marginBottom:'14px' }}>
        <button onClick={()=>setShowSaved(!showSaved)} style={S.btn(showSaved?'#0891b2':'#f8fafc', showSaved?'#fff':'#334155')}>
          📋 {showSaved?'Hide':'Show'} Saved Cut Plans ({(db?.cutPlans||[]).filter(p=>!setup.style||p.style===setup.style).length})
        </button>
        <label style={{ ...S.btn('#f8fafc','#334155'), border:'1px solid #cbd5e1', display:'flex', alignItems:'center', gap:'6px' }}>
          📊 Import Markers CSV
          <input type="file" accept=".csv" onChange={importMarkersCSV} style={{display:'none'}}/>
        </label>
      </div>

      {/* ── Saved Plans Panel ── */}
      {showSaved && <SavedPlansPanel db={db} setup={setup} layStatus={layStatus} setLayStatus={setLayStatus} printPlan={printPlan}/>}

      {/* ════════════════ STEP 1: SETUP ════════════════ */}
      {step >= 1 && (
        <div style={S.card}>
          <div style={S.hdr}><span>STEP 1 — ORDER SETUP</span>
            <span style={{ fontSize:'0.72rem', color:'#16a34a' }}>{sizeBreakdown.length>0?'✓ Size Breakdown Loaded':''}</span>
          </div>
          <div style={S.grid('repeat(5,1fr)')}>
            <Fld label="Brand">
              <select style={S.sel} value={setup.brand} onChange={e=>setSetup(s=>({...s,brand:e.target.value,style:'',po:'',color:''}))}>
                <option value="">All</option>
                {brands.map(b=><option key={b.id||b.name}>{b.name}</option>)}
              </select>
            </Fld>
            <Fld label="Season">
              <select style={S.sel} value={setup.season} onChange={e=>setSetup(s=>({...s,season:e.target.value}))}>
                <option value="">All</option>
                {seasons.map(s=><option key={s.id||s.name}>{s.name}</option>)}
              </select>
            </Fld>
            <Fld label="Style">
              <select style={S.sel} value={setup.style} onChange={e=>{
                setSetup(s=>({...s,style:e.target.value,po:'',color:''}));
                setSizeBreakdown([]); setEligibleRolls([]); setScenarios(null);
              }}>
                <option value="">— Select —</option>
                {stylesForBrand.map(s=><option key={s.id||s.styleRef} value={s.styleRef}>{s.styleRef}</option>)}
              </select>
            </Fld>
            <Fld label="PO Number">
              <select style={S.sel} value={setup.po} onChange={e=>{
                setSetup(s=>({...s,po:e.target.value}));
                if (setup.style) loadSizeBreakdown(setup.style, e.target.value, setup.color);
              }}>
                <option value="">— Select —</option>
                {(typeof poOptions[0]==='string' ? poOptions.map(p=>({num:p,label:p})) : poOptions)
                  .map(p=><option key={p.num} value={p.num}>{p.label||p.num}</option>)}
              </select>
            </Fld>
            <Fld label="Color">
              <select style={S.sel} value={setup.color} onChange={e=>{
                setSetup(s=>({...s,color:e.target.value}));
                if (setup.style) loadSizeBreakdown(setup.style, setup.po, e.target.value);
              }}>
                <option value="">— Select —</option>
                {colorOptions.map(c=><option key={c}>{c}</option>)}
              </select>
            </Fld>
          </div>

          {/* Size breakdown display (locked to PO) */}
          {sizeBreakdown.length > 0 && (
            <div style={{ marginTop:'14px', background:'#f0fdf4', border:'1px solid #86efac', borderRadius:'8px', padding:'14px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px' }}>
                <strong style={{ color:'#166534', fontSize:'0.85rem' }}>📋 SIZE BREAKDOWN — FROM PO (Locked)</strong>
                <button onClick={()=>loadSizeBreakdown(setup.style,setup.po,setup.color)} style={S.btn('#eff6ff','#2563eb')}>🔄 Reload</button>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'10px', alignItems:'center' }}>
                {sizeBreakdown.map(s => {
                  const cut = alreadyCut[s.size]||0;
                  const balance = (parseInt(s.qty)||0) - cut;
                  return (
                    <div key={s.size} style={{ textAlign:'center', background:'white', border:'1px solid #86efac', borderRadius:'6px', padding:'8px 14px', minWidth:'70px' }}>
                      <div style={{ fontSize:'0.72rem', fontWeight:'800', color:'#166534' }}>{s.size}</div>
                      <div style={{ fontSize:'1.1rem', fontWeight:'900', color:'#1e3a8a' }}>{fmtK(s.qty)}</div>
                      {cut>0 && <div style={{ fontSize:'0.68rem', color:'#16a34a' }}>cut: {fmtK(cut)}</div>}
                      {balance>0 && <div style={{ fontSize:'0.68rem', color:'#dc2626', fontWeight:'700' }}>bal: {fmtK(balance)}</div>}
                    </div>
                  );
                })}
                <div style={{ marginLeft:'auto', textAlign:'right' }}>
                  <div style={{ fontWeight:'900', color:'#1e3a8a', fontSize:'1rem' }}>
                    {fmtK(sizeBreakdown.reduce((a,s)=>a+(parseInt(s.qty)||0),0))} pcs
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'#64748b' }}>PO total</div>
                </div>
              </div>
            </div>
          )}
          {sizeBreakdown.length === 0 && setup.style && (
            <div style={{ marginTop:'12px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:'6px', padding:'12px', fontSize:'0.82rem', color:'#991b1b' }}>
              ⚠ No size data found. Select a PO or go to <strong>Merchandising → PO Upload</strong> to upload PO size table.
            </div>
          )}
          <div style={{ marginTop:'16px', textAlign:'right' }}>
            <button onClick={()=>{ if(!setup.style||!setup.color||!sizeBreakdown.length){ setAlert('⚠ Select Style, Color and ensure PO size data is loaded.'); return; } setStep(2); }}
              style={S.btn('#0891b2')}>Next: Marker Plan →</button>
          </div>
        </div>
      )}

      {/* ════════════════ STEP 2: MARKERS ════════════════ */}
      {step >= 2 && (
        <div style={S.card}>
          <div style={S.hdr}><span>STEP 2 — MARKER PLAN</span>
            <span style={S.badge('#dbeafe','#1e40af')}>{markers.length} marker(s)</span>
          </div>
          {/* Marker add form */}
          <div style={{ ...S.grid('repeat(6,1fr) auto'), marginBottom:'12px', alignItems:'end', gap:'8px' }}>
            {[['Marker No','markerNo'],['Pattern Code','patternCode'],['Pattern Grp','patternGroup'],['Width (in)','width'],['Length (m)','length'],['Effcy%','efficiency'],['m/pc','consumption']].map(([l,k])=>(
              <Fld key={k} label={l}>
                <input style={S.inp} value={markerDraft[k]} onChange={e=>setMarkerDraft(d=>({...d,[k]:e.target.value}))}/>
              </Fld>
            ))}
          </div>
          <div style={{ ...S.grid('repeat(5,1fr) auto'), marginBottom:'14px', alignItems:'end', gap:'8px' }}>
            {[['Way','way','1-Way,2-Way,N-Way'],['CSV Type','csvType',''],['Wastage%','wastage',''],['Shrink L%','shrinkageL',''],['Shrink W%','shrinkageW','']].map(([l,k,opts2])=>(
              <Fld key={k} label={l}>
                {opts2 ? <select style={S.sel} value={markerDraft[k]} onChange={e=>setMarkerDraft(d=>({...d,[k]:e.target.value}))}>
                  {opts2.split(',').map(o=><option key={o}>{o}</option>)}
                </select> : <input style={S.inp} value={markerDraft[k]} onChange={e=>setMarkerDraft(d=>({...d,[k]:e.target.value}))}/>}
              </Fld>
            ))}
          </div>
          <Fld label="Cut Panels (e.g. S-1 XL-2 XXL-1 or S/1 XL/2 XXL/1)">
            <input style={{ ...S.inp, fontFamily:'monospace', fontSize:'1rem' }} value={markerDraft.cutPanels}
              onChange={e => setMarkerDraft(d => ({
                ...d,
                cutPanels: e.target.value
                  .replace(/[–—−‐‑]/g, '-')   // normalize dashes
                  .replace(/\s*\/\s*/g, '-')   // normalize slash to hyphen
                  .toUpperCase()
              }))}
              placeholder="S-1 XL-2 XXL-1"/>
          </Fld>
          {markerDraft.cutPanels && (
            <div style={{ marginTop:'6px', display:'flex', gap:'6px', flexWrap:'wrap' }}>
              {parsePanels(markerDraft.cutPanels).map(p=>(
                <span key={p.size} style={S.pill('#2563eb')}>{p.size}×{p.qty}</span>
              ))}
              <span style={{ fontSize:'0.78rem', color:'#64748b', marginLeft:'8px' }}>
                = {parsePanels(markerDraft.cutPanels).reduce((s,p)=>s+p.qty,0)} pcs/ply
              </span>
            </div>
          )}
          <div style={{ marginTop:'12px', display:'flex', gap:'8px' }}>
            <button onClick={()=>{
              if(!markerDraft.markerNo||!markerDraft.cutPanels){ setAlert('⚠ Marker No and Cut Panels are required.'); return; }
              setMarkers(prev=>[...prev.filter(m=>m.markerNo!==markerDraft.markerNo), {...markerDraft}]);
              setMarkerDraft(d=>({...d,markerNo:'',cutPanels:'',efficiency:'',consumption:''}));
            }} style={S.btn('#059669')}>+ Add Marker</button>
          </div>

          {/* Markers table */}
          {markers.length > 0 && (
            <div style={{ marginTop:'14px', overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  {['Marker','Pattern Code','Pattern Grp','Width','Length','Way','CSV','Cut Panels','Eff%','m/pc','Pcs/ply','Action'].map(h=>(
                    <th key={h} style={S.TH}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {markers.map(m=>{
                    const pps = parsePanels(m.cutPanels).reduce((s,p)=>s+p.qty,0);
                    return (
                      <tr key={m.markerNo}>
                        <td style={{...S.TD,fontWeight:'800',color:'#2563eb'}}>{m.markerNo}</td>
                        <td style={S.TD}>{m.patternCode}</td>
                        <td style={{...S.TD,color:'#7c3aed',fontWeight:'bold'}}>{m.patternGroup||'—'}</td>
                        <td style={S.TD}>{toInches(m.width)}"
                          {parseFloat(m.width) > 100 && <span style={{fontSize:'0.65rem',color:'#94a3b8'}}> ({m.width}cm)</span>}
                        </td>
                        <td style={S.TD}>{m.length}m</td>
                        <td style={S.TD}>{m.way}</td>
                        <td style={S.TD}>{m.csvType||'—'}</td>
                        <td style={{...S.TD,fontFamily:'monospace'}}>{m.cutPanels}</td>
                        <td style={{...S.TD,textAlign:'right',color:'#059669',fontWeight:'700'}}>{m.efficiency}%</td>
                        <td style={{...S.TD,textAlign:'right',fontWeight:'700'}}>{m.consumption}</td>
                        <td style={{...S.TD,textAlign:'right',fontWeight:'800',color:'#7c3aed'}}>{pps}</td>
                        <td style={S.TD}><button onClick={()=>setMarkers(prev=>prev.filter(x=>x.markerNo!==m.markerNo))} style={{...S.btn('#fee2e2','#dc2626'),padding:'3px 8px',fontSize:'0.72rem'}}>✕</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop:'16px', textAlign:'right' }}>
            <button onClick={()=>{ if(!markers.length){ setAlert('⚠ Add at least one marker.'); return; } scanRolls(); setStep(3); }}
              style={S.btn('#0891b2')}>Next: Scan Rolls →</button>
          </div>
        </div>
      )}

      {/* ════════════════ STEP 3: ROLLS ════════════════ */}
      {step >= 3 && (
        <div style={S.card}>
          <div style={S.hdr}><span>STEP 3 — ROLL INVENTORY & CONSTRAINTS</span>
            <span style={S.badge('#dbeafe','#1e40af')}>{eligibleRolls.length} rolls matched</span>
          </div>
          <div style={{ ...S.grid('repeat(4,1fr)'), marginBottom:'14px', alignItems:'end' }}>
            <Fld label="Max Plies / Lay">
              <input type="number" style={S.inp} value={opts.maxPlies} onChange={e=>setOpts(o=>({...o,maxPlies:e.target.value}))}/>
            </Fld>
            <Fld label="Ply Deviation (±)">
              <input type="number" style={S.inp} value={opts.pliesDev} onChange={e=>setOpts(o=>({...o,pliesDev:e.target.value}))}/>
            </Fld>
            <div style={{ display:'flex', flexDirection:'column', gap:'8px', justifyContent:'flex-end' }}>
              {[['ignoreShade','Ignore Shade (mix shades)'],['ignoreCSV','Ignore CSV Group']].map(([k,l])=>(
                <label key={k} style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.82rem', cursor:'pointer' }}>
                  <input type="checkbox" checked={opts[k]} onChange={e=>setOpts(o=>({...o,[k]:e.target.checked}))}/>
                  <span style={{ color: opts[k]?'#dc2626':'#334155' }}>{l}</span>
                </label>
              ))}
            </div>
            <button onClick={scanRolls} style={{...S.btn('#0891b2'),alignSelf:'flex-end'}}>🔄 Re-Scan Rolls</button>
          </div>

          {eligibleRolls.length > 0 && (
            <div style={{ overflowX:'auto', border:'1px solid #e2e8f0', borderRadius:'8px' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
                <thead><tr>
                  {['Roll ID','Color','Shade','Width','Balance(m)','L.Shrink%','W.Shrink%','Pattern Grp','CSV Grp','Rack','Cell','Status'].map(h=>(
                    <th key={h} style={S.TH}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {eligibleRolls.map((r,i)=>(
                    <tr key={getRollId(r)||i} style={{ background:i%2===0?'white':'#fafafa' }}>
                      <td style={{...S.TD,fontWeight:'800',color:'#2563eb'}}>{getRollId(r)}</td>
                      <td style={S.TD}>{getRollColor(r)||'—'}</td>
                      <td style={{...S.TD,fontWeight:'700',color:'#7c3aed'}}>{getRollShade(r)||'—'}</td>
                      <td style={S.TD}>{getRollWidth(r)?`${getRollWidth(r)}"` : '—'}</td>
                      <td style={{...S.TD,fontWeight:'700',color:'#059669',textAlign:'right'}}>{getBalance(r).toFixed(1)}m</td>
                      <td style={{...S.TD,textAlign:'center',color:getLengthShrink(r)>0?'#dc2626':'#94a3b8'}}>{getLengthShrink(r)>0?`${(getLengthShrink(r)*100).toFixed(0)}%`:'—'}</td>
                      <td style={{...S.TD,textAlign:'center',color:getWidthShrink(r)>0?'#dc2626':'#94a3b8'}}>{getWidthShrink(r)>0?`${(getWidthShrink(r)*100).toFixed(0)}%`:'—'}</td>
                      <td style={S.TD}>{getPatternGroup(r)||'—'}</td>
                      <td style={S.TD}>{getCSVGroup(r)||'—'}</td>
                      <td style={S.TD}>{r.Rack_No||'—'}</td>
                      <td style={S.TD}>{r.Cell_No||'—'}</td>
                      <td style={S.TD}><span style={S.badge('#dcfce7','#166534')}>READY</span></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr style={{ background:'#f0fdf4', fontWeight:'bold' }}>
                  <td colSpan={4} style={{...S.TD,fontWeight:'800'}}>TOTAL — {eligibleRolls.length} rolls</td>
                  <td style={{...S.TD,fontWeight:'800',color:'#059669',textAlign:'right'}}>
                    {fmtN(eligibleRolls.reduce((s,r) => s + getBalance(r), 0))}m
                  </td>
                  <td colSpan={6} style={S.TD}></td>
                </tr></tfoot>
              </table>
            </div>
          )}
          <div style={{ marginTop:'16px', textAlign:'right' }}>
            <button onClick={()=>{ if(!eligibleRolls.length){ setAlert('⚠ No rolls found. Check style/color/inventory.'); return; } runSim(); }}
              style={S.btn('#7c3aed')}>⚡ Run Simulation →</button>
          </div>
        </div>
      )}

      {/* ════════════════ STEP 4: SIMULATION RESULTS ════════════════ */}
      {step >= 4 && scenarios && (
        <div style={S.card}>
          <div style={S.hdr}><span>STEP 4 — SIMULATION RESULTS (Select Best Plan)</span>
            <span style={{ fontSize:'0.72rem', color:'#64748b' }}>Sorted by lowest overall consumption</span>
          </div>

          {/* Scenario comparison cards */}
          <div style={S.grid('repeat(3,1fr)')}>
            {scenarios.map((sc, idx) => {
              const r = sc.result;
              const isSel = selectedScenario?.id === sc.id;
              return (
                <div key={sc.id} onClick={()=>setSelectedScenario(sc)}
                  style={{ border:`2px solid ${isSel?'#2563eb':'#e2e8f0'}`, borderRadius:'10px', padding:'14px', cursor:'pointer',
                           background: isSel?'#eff6ff':'white', transition:'all .15s' }}>
                  {idx===0 && <div style={{ ...S.badge('#dcfce7','#166534'), marginBottom:'8px' }}>⭐ BEST</div>}
                  <div style={{ fontWeight:'800', fontSize:'0.9rem', color:'#1e3a8a', marginBottom:'8px' }}>{sc.label}</div>
                  <div style={{ fontSize:'0.76rem', color:'#64748b', marginBottom:'10px' }}>{sc.desc}</div>
                  <div style={S.grid('1fr 1fr')}>
                    {[
                      ['Overall m/pc', r.overallConsumption, '#1e3a8a', '1.4rem'],
                      ['Net m/pc', r.netConsumption, '#059669', '1.1rem'],
                      ['Total Fabric', r.totalFabric+'m', '#334155', '1rem'],
                      ['End-Bits', r.totalEndBit+'m ('+r.endBitPct+'%)', '#dc2626', '0.9rem'],
                    ].map(([l,v,c,fs])=>(
                      <div key={l} style={{ textAlign:'center', background:'#f8fafc', borderRadius:'6px', padding:'6px' }}>
                        <div style={{ fontSize: fs, fontWeight:'900', color: c }}>{v}</div>
                        <div style={{ fontSize:'0.65rem', color:'#94a3b8' }}>{l}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop:'10px', display:'flex', gap:'6px', flexWrap:'wrap' }}>
                    <span style={S.badge(r.isComplete?'#dcfce7':'#fee2e2', r.isComplete?'#166534':'#dc2626')}>
                      {r.isComplete?'✓ COMPLETE':'⚠ SHORT'}
                    </span>
                    <span style={S.badge('#dbeafe','#1e40af')}>{r.steps.length} steps</span>
                    <span style={S.badge('#f5f3ff','#7c3aed')}>{fmtK(r.totalPcs)} pcs</span>
                  </div>
                  {/* Markers used */}
                  <div style={{ marginTop:'8px', fontSize:'0.72rem', color:'#64748b' }}>
                    Markers: {r.markersUsed.join(' → ')}
                  </div>
                  {/* Short sizes */}
                  {!r.isComplete && (
                    <div style={{ marginTop:'6px', background:'#fef2f2', borderRadius:'4px', padding:'6px', fontSize:'0.72rem', color:'#dc2626' }}>
                      SHORT: {Object.entries(r.remaining).filter(([,q])=>q>0).map(([s,q])=>`${s}: ${q}`).join(', ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selected scenario detail */}
          {selectedScenario && (
            <div style={{ marginTop:'16px' }}>
              <div style={{ fontWeight:'800', fontSize:'0.9rem', color:'#1e3a8a', marginBottom:'12px' }}>
                📋 PLAN DETAIL — {selectedScenario.label}
              </div>
              {selectedScenario.result.steps.map(st => (
                <div key={st.stepNo} style={{ marginBottom:'14px', border:'1px solid #e2e8f0', borderRadius:'8px', overflow:'hidden' }}>
                  <div style={{ background:'#f8fafc', padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <strong style={{ color:'#2563eb' }}>Step {st.stepNo}: Marker {st.markerNo}</strong>
                      <span style={{ marginLeft:'10px', fontSize:'0.78rem', color:'#64748b' }}>
                        {st.laysCount} lays · {st.totalPlies} plies · {fmtK(st.totalPcsStep)} pcs · {st.fabricUsed}m fabric · {st.endBit}m end-bit
                      </span>
                    </div>
                    <div style={{ display:'flex', gap:'6px' }}>
                      <span style={S.pill('#059669')}>{st.efficiency}% eff</span>
                      <span style={S.pill('#2563eb')}>{st.consumption} m/pc</span>
                    </div>
                  </div>
                  {/* Pcs breakdown for this step */}
                  <div style={{ padding:'10px 14px', display:'flex', gap:'8px', flexWrap:'wrap', borderBottom:'1px solid #f1f5f9' }}>
                    {Object.entries(st.pcsProduced).map(([sz,q])=>(
                      <span key={sz} style={S.pill('#7c3aed')}>{sz}: {fmtK(q)} pcs</span>
                    ))}
                  </div>
                  {/* Roll table — MM-style per-roll summary (no "continues") */}
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem' }}>
                      <thead><tr>
                        {['Roll ID','Shade','Width','Balance','No.Lays','Full Lays','Lays Used','Lay Range','Used (m)','End Bit (m)','End Bit %','Rack/Cell','Partial?'].map(h=>(
                          <th key={h} style={{...S.TH, background: h.includes('End Bit')?'#fef2f2': h==='Used (m)'?'#f0fdf4': undefined}}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {(st.lays||[]).map((r, ri) => {
                          const isEB = r.isEndBitLay;
                          const hasEB = parseFloat(r.wasteFromRoll||r.endBit||0) > 0.001;
                          return (
                            <tr key={ri} style={{ background: isEB?'#fef9c3': hasEB?'#fff8f8': 'white', borderBottom:'1px solid #f1f5f9' }}>
                              <td style={{...S.TD,fontWeight:'800',color:isEB?'#92400e':'#2563eb',fontFamily:'monospace'}}>
                                {isEB ? '♻ '+r.rollId : r.rollId}
                              </td>
                              <td style={{...S.TD,color:'#7c3aed',fontWeight:'700'}}>{r.shade||'—'}</td>
                              <td style={S.TD}>{r.width ? r.width+'"' : '—'}</td>
                              <td style={{...S.TD,textAlign:'right',fontWeight:'700'}}>{r.balance ? r.balance+'m' : '—'}</td>
                              <td style={{...S.TD,textAlign:'right',color:'#64748b'}}>{r.noLays||'—'}</td>
                              <td style={{...S.TD,textAlign:'right',fontWeight:'700',color:'#059669'}}>{r.fullLays??'—'}</td>
                              <td style={{...S.TD,textAlign:'right',fontWeight:'800',color:'#2563eb'}}>{r.laysUsed??'—'}</td>
                              <td style={{...S.TD,fontSize:'0.7rem',color:'#475569'}}>
                                {r.layFrom ? `L${r.layFrom}–L${r.layTo}` : '—'}
                              </td>
                              <td style={{...S.TD,textAlign:'right',fontWeight:'700',background:'#f0fdf4',color:'#16a34a'}}>
                                {r.usedFromRoll ? r.usedFromRoll+'m' : '—'}
                              </td>
                              <td style={{...S.TD,textAlign:'right',fontWeight:'800',background:'#fef2f2',
                                  color: hasEB ? '#dc2626' : '#16a34a'}}>
                                {hasEB ? (r.wasteFromRoll||r.endBit)+'m' : '0.00m'}
                              </td>
                              <td style={{...S.TD,textAlign:'right',color: parseFloat(r.endBitPct||0)>10?'#dc2626':'#64748b'}}>
                                {r.endBitPct ? r.endBitPct+'%' : '—'}
                              </td>
                              <td style={{...S.TD,fontSize:'0.7rem'}}>
                                {r.rack&&r.cell ? `R${r.rack}/C${r.cell}` : r.rack||'—'}
                              </td>
                              <td style={{...S.TD,fontSize:'0.7rem',color: r.isPartial?'#d97706':'#16a34a'}}>
                                {r.isPartial ? '↩ partial' : '✓ full'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{fontSize:'0.68rem',color:'#64748b',padding:'4px 8px',background:'#fafafa',borderTop:'1px solid #f1f5f9'}}>
                      No.Lays = balance ÷ lay length (decimal) &nbsp;|&nbsp;
                      Full Lays = floor(No.Lays) &nbsp;|&nbsp;
                      End Bit = balance − Full Lays × lay length &nbsp;|&nbsp;
                      ↩ partial = only some lays used, remainder stays on shelf
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedScenario && (
            <div style={{ marginTop:'16px', display:'flex', justifyContent:'flex-end', gap:'10px' }}>
              <button onClick={()=>confirmPlan(selectedScenario)} style={S.btn('#16a34a')}>
                ✅ Confirm & Save Plan
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Saved Plans Panel ────────────────────────────────────────────────────────
function SavedPlansPanel({ db, setup, layStatus, setLayStatus, printPlan }) {
  const plans = (db?.cutPlans || db?.stylePlans || []).filter(p =>
    p.type === 'CUT_PLAN' && (!setup.style || p.style === setup.style));
  const fmtK = n => (parseInt(n)||0).toLocaleString();

  // Assortment status: PO+Color → size → {auth, cut}
  const assortment = plans.reduce((acc, p) => {
    const k = `${p.po}|${p.color}`;
    if (!acc[k]) acc[k] = { po:p.po, color:p.color, style:p.style, sizes:{} };
    (p.sizeBreakdown||[]).forEach(s=>{
      if (!acc[k].sizes[s.size]) acc[k].sizes[s.size] = { auth:parseInt(s.qty)||0, cut:0 };
    });
    Object.entries(p.pcsComplete||p.totalPieces||{}).forEach(([sz,q])=>{
      if (!acc[k].sizes[sz]) acc[k].sizes[sz] = { auth:0, cut:0 };
      acc[k].sizes[sz].cut += (parseInt(q)||0);
    });
    return acc;
  }, {});

  if (!plans.length) return (
    <div style={{ background:'white', border:'1px solid #e2e8f0', borderRadius:'8px', padding:'20px', textAlign:'center', color:'#94a3b8', marginBottom:'14px' }}>
      No saved cut plans yet. Run the simulation to create one.
    </div>
  );

  return (
    <div style={{ marginBottom:'14px', border:'2px solid #0891b2', borderRadius:'8px', overflow:'hidden' }}>
      <div style={{ background:'#0891b2', color:'white', padding:'10px 16px', fontWeight:'800', fontSize:'0.85rem', display:'flex', justifyContent:'space-between' }}>
        <span>📋 SAVED CUT PLANS — {plans.length} plan(s)</span>
      </div>

      {/* Assortment status */}
      <div style={{ padding:'12px 14px', background:'#f0fdfa', borderBottom:'1px solid #ccfbf1' }}>
        <div style={{ fontWeight:'800', color:'#0f766e', fontSize:'0.78rem', marginBottom:'8px' }}>
          📦 ORDER ASSORTMENT STATUS — PO · COLOR · SIZE
        </div>
        {Object.values(assortment).map((a,i)=>(
          <div key={i} style={{ marginBottom:'8px', background:'white', border:'1px solid #e2e8f0', borderRadius:'6px', padding:'8px 12px' }}>
            <strong style={{ fontSize:'0.8rem', color:'#1e3a8a' }}>PO: {a.po} · {a.color} ({a.style})</strong>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'5px' }}>
              {Object.entries(a.sizes).map(([sz,{auth,cut}])=>{
                const pct  = auth>0?Math.round(cut/auth*100):0;
                const done = cut>=auth;
                return (
                  <div key={sz} style={{ padding:'4px 10px', borderRadius:'6px',
                    background:done?'#dcfce7':cut>0?'#fef3c7':'#fef2f2',
                    border:`1px solid ${done?'#86efac':cut>0?'#fcd34d':'#fca5a5'}`, fontSize:'0.78rem' }}>
                    <strong style={{ color:'#1e3a8a' }}>{sz}</strong>
                    <span style={{ color:done?'#166534':cut>0?'#92400e':'#dc2626', marginLeft:'6px' }}>
                      {fmtK(cut)}/{fmtK(auth)} ({pct}%)
                    </span>
                    {done && ' ✓'}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Plans table */}
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
          <thead><tr style={{ background:'#f0fdfa' }}>
            {['Style','PO','Color','Strategy','Markers','Lays','Pcs','Consumption','Status','Date','Actions'].map(h=>(
              <th key={h} style={{ padding:'7px 10px', textAlign:'left', borderBottom:'1px solid #ccfbf1', fontWeight:'700', color:'#0f766e' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {plans.map((plan,i)=>{
              const totalLays = (plan.steps||[]).reduce((s,st)=>s+st.laysCount,0);
              const done = Object.values(layStatus[plan.id]||{}).filter(s=>s==='DONE').length;
              const pct  = totalLays>0?Math.round(done/totalLays*100):0;
              return (
                <tr key={plan.id||i} style={{ borderBottom:'1px solid #f1f5f9', background:i%2===0?'white':'#f8fafc' }}>
                  <td style={{ padding:'7px 10px', fontWeight:'700' }}>{plan.style}</td>
                  <td style={{ padding:'7px 10px' }}>{plan.po}</td>
                  <td style={{ padding:'7px 10px' }}>{plan.color}</td>
                  <td style={{ padding:'7px 10px', fontSize:'0.72rem' }}>{plan.strategy||'—'}</td>
                  <td style={{ padding:'7px 10px', fontFamily:'monospace', fontSize:'0.72rem' }}>{(plan.markersUsed||[]).join(', ')}</td>
                  <td style={{ padding:'7px 10px', textAlign:'right' }}>{totalLays}</td>
                  <td style={{ padding:'7px 10px', textAlign:'right', fontWeight:'700' }}>{fmtK(plan.totalPcs)}</td>
                  <td style={{ padding:'7px 10px', textAlign:'right', color:'#1e3a8a', fontWeight:'700' }}>{plan.overallConsumption} m/pc</td>
                  <td style={{ padding:'7px 10px' }}>
                    <span style={{ padding:'2px 8px', borderRadius:'12px', fontSize:'0.7rem', fontWeight:'700',
                      background:plan.status==='READY'?'#dcfce7':'#fee2e2',
                      color:plan.status==='READY'?'#166534':'#dc2626' }}>{plan.status}</span>
                  </td>
                  <td style={{ padding:'7px 10px', fontSize:'0.7rem', color:'#94a3b8' }}>{(plan.generatedAt||'').slice(0,16)}</td>
                  <td style={{ padding:'7px 10px' }}>
                    <button onClick={()=>printPlan(plan)}
                      style={{ padding:'3px 8px', background:'#f5f3ff', color:'#7c3aed', border:'1px solid #ddd8fe', borderRadius:'5px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'700' }}>
                      🖨 Report
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
